import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
} from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import ListsManager from '../services/ListsManager';
import PercentageLoader from '../components/PercentageLoader';
import { useTranslation } from 'react-i18next';

const ConfirmationCodeScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { phoneNumber, listID, listName } = location.state || {};

    const [code, setCode] = useState('');
    const [isConfirmButtonVisible, setIsConfirmButtonVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const codeInputRef = useRef(null);

    useEffect(() => {
        // Focus the code input when the screen appears
        setTimeout(() => {
            if (codeInputRef.current) {
                codeInputRef.current.focus();
            }
        }, 100);
    }, []);

    // Update confirm button visibility when code changes
    useEffect(() => {
        setIsConfirmButtonVisible(code.length >= 5); // Visible if code is at least 5 digits
    }, [code]);

    const handleCodeChange = (text) => {
        // Only allow digits
        const validText = text.replace(/[^0-9]/g, '');

        // Limit length to 5 characters
        if (validText.length <= 5) {
            setCode(validText);
        }
    };

    const handleConfirm = async () => {
        try {
            setIsLoading(true);
            setError('');

            // Call the backend to verify the code
            const result = await ListsManager.verifyCode(phoneNumber, code, listID);

            if (result.success) {
                if (result.channels && result.channels.length > 0) {
                    // Navigate to channel selection screen
                    navigate('/import-telegram', {
                        state: {
                            phoneNumber,
                            listID,
                            listName,
                            channels: result.channels
                        }
                    });
                } else {
                    setError(t('app.confirmation.verificationSuccessNoChannels'));
                }
            } else {
                setError(t('app.confirmation.invalidCode'));
            }
        } catch (error) {
            console.error('Error verifying code:', error);
            setError(t('app.confirmation.verificationFailed', { error: error.message || 'Unknown error' }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={handleBack}
                >
                    <Text style={styles.backButtonText}>{t('app.common.back')}</Text>
                </TouchableOpacity>
            </View>

            <View style={styles.content}>
                <Text style={styles.title}>{t('app.confirmation.title')}</Text>
                <Text style={styles.subtitle}>
                    {t('app.confirmation.subtitle', { phoneNumber })}
                </Text>

                <TextInput
                    ref={codeInputRef}
                    style={styles.codeInput}
                    value={code}
                    onChangeText={handleCodeChange}
                    keyboardType="number-pad"
                    placeholder="12345"
                    placeholderTextColor="#666"
                    maxLength={5}
                />

                {error ? <Text style={styles.errorText}>{error}</Text> : null}

                {isLoading ? (
                    <View style={styles.loadingContainer}>
                        <PercentageLoader fontSize={16} color="#0066cc" />
                        <Text style={styles.loadingText}>{t('app.confirmation.verifyingCode')}</Text>
                    </View>
                ) : isConfirmButtonVisible && (
                    <TouchableOpacity
                        style={styles.confirmButton}
                        onPress={handleConfirm}
                    >
                        <Text style={styles.confirmButtonText}>{t('app.confirmation.verify')}</Text>
                    </TouchableOpacity>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'var(--bg-deep-void)',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255, 255, 255, 0.08)',
        backgroundColor: 'var(--bg-card)',
    },
    backButton: {
        padding: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        transition: 'all 0.2s',
        cursor: 'pointer',
    },
    backButtonText: {
        color: 'var(--text-primary)',
        fontSize: 16,
        fontWeight: '600',
    },
    content: {
        flex: 1,
        padding: 32,
        alignItems: 'center',
    },
    title: {
        fontSize: 28,
        fontWeight: '700',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: 12,
        marginTop: 24,
        fontFamily: 'Outfit, sans-serif',
        letterSpacing: '-0.01em',
    },
    subtitle: {
        fontSize: 16,
        color: 'var(--text-secondary)',
        textAlign: 'center',
        marginBottom: 48,
        maxWidth: 400,
        lineHeight: 24,
    },
    codeInput: {
        fontSize: 36,
        fontWeight: '700',
        color: 'var(--text-primary)',
        borderBottomWidth: 2,
        borderBottomColor: 'var(--accent-primary)',
        paddingVertical: 16,
        marginBottom: 64,
        textAlign: 'center',
        letterSpacing: 12,
        width: '100%',
        maxWidth: 240,
        backgroundColor: 'transparent',
        outlineStyle: 'none',
        fontFamily: 'monospace',
    },
    confirmButton: {
        backgroundColor: 'var(--accent-primary)',
        borderRadius: 20,
        paddingVertical: 18,
        alignItems: 'center',
        width: '100%',
        maxWidth: 200,
        alignSelf: 'center',
        marginTop: 24,
        boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
        cursor: 'pointer',
        transition: 'transform 0.1s',
    },
    confirmButtonText: {
        color: 'white',
        fontSize: 18,
        fontWeight: '700',
        letterSpacing: '0.05em',
    },
    errorText: {
        color: 'var(--accent-warning)',
        marginBottom: 32,
        textAlign: 'center',
        fontSize: 16,
        backgroundColor: 'rgba(244, 63, 94, 0.1)',
        padding: 16,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(244, 63, 94, 0.2)',
        width: '100%',
        maxWidth: 400,
    },
    loadingContainer: {
        marginTop: 32,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        padding: 16,
        borderRadius: 16,
    },
    loadingText: {
        color: 'var(--text-secondary)',
        marginLeft: 12,
        fontSize: 16,
    },
});

export default ConfirmationCodeScreen; 